import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useLang } from '@/i18n/LanguageContext';
import { EASE } from '@/components/MaskLines';

// Colora in verde solo l'ultima parola della riga (es. "A SOLUTION." → SOLUTION.)
function HighlightLast({ text }) {
  const cut = text.lastIndexOf(' ');
  if (cut === -1) return <span className="text-[var(--pz-green)]">{text}</span>;
  return (
    <>
      {text.slice(0, cut + 1)}
      <span className="text-[var(--pz-green)]">{text.slice(cut + 1)}</span>
    </>
  );
}

export default function Opening() {
  const { t } = useLang();

  const heroRef = useRef(null);
  const { scrollYProgress: heroProg } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(heroProg, [0, 1], ['0%', '-16%']);
  const heroOpacity = useTransform(heroProg, [0, 0.9], [1, 0]);

  const lines = t.payoff.lines;
  // su desktop la riga più lunga deve stare in una riga sola (IT "UNA SOLUZIONE." è più lunga di EN)
  const longest = Math.max(...lines.map((l) => l.split(String.fromCharCode(0xad)).join('').length));
  const fit = `${(88 / (longest * 0.75)).toFixed(2)}vw`;

  return (
    <section id="top" ref={heroRef} className="relative" data-testid="section-opening">
      <div className="relative flex h-[100svh] flex-col justify-center px-5 md:px-10">
        <motion.div style={{ y: heroY, opacity: heroOpacity }}>
          <h1
            data-testid="payoff-lines"
            className="font-display text-[14vw] md:text-[min(10.5vw,var(--hero-fit))]"
            style={{ '--hero-fit': fit }}
          >
            {lines.map((line, i) => (
              <span className="mask-line" key={i}>
                <motion.span
                  className="block"
                  initial={{ y: '112%' }}
                  animate={{ y: '0%' }}
                  transition={{ duration: 1.15, ease: EASE, delay: 0.2 + i * 0.15 }}
                >
                  {i === lines.length - 1 ? <HighlightLast text={line} /> : line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-8 flex items-center gap-6 font-mono-label text-white/60"
            data-testid="hero-meta"
          >
            {t.hero.meta}
            <span className="hidden md:block h-px flex-1 bg-white/20" aria-hidden="true" />
          </motion.p>
        </motion.div>

        <p className="absolute bottom-8 left-1/2 -translate-x-1/2 font-mono-label text-white/40" data-testid="hero-scroll-hint">
          {t.hero.scroll} ↓
        </p>
      </div>
    </section>
  );
}
